import { onMounted, ref, toValue, watch } from 'vue'
import type { MaybeRefOrGetter } from 'vue'

import { useCookieStore } from '~/store/cookiesStore'
import type { TypeVisibilitySettingsWithoutUnresolvableLinksResponse } from '~/types/contentful/TypeVisibilitySettings'
import type { VisibilityDecision } from '~/types/visibility'

export type PageVisibilityStatus = 'checking' | 'visible' | 'unknown'

type DeniedVisibilityDecision = Extract<VisibilityDecision, { visible: false }>

interface UsePageVisibilityOptions {
  visibility: MaybeRefOrGetter<
    TypeVisibilitySettingsWithoutUnresolvableLinksResponse | null | undefined
  >
  onDenied: (decision: DeniedVisibilityDecision) => unknown
  debug?: MaybeRefOrGetter<boolean | undefined>
}

export function usePageVisibility({
  visibility,
  onDenied,
  debug,
}: UsePageVisibilityOptions) {
  const { $visibilityDecision } = useNuxtApp()
  const cookieStore = useCookieStore()

  const visibilityStatus = ref<PageVisibilityStatus>(
    toValue(visibility) ? 'checking' : 'visible',
  )

  const runVisibilityCheck = async () => {
    const visibilitySettings = toValue(visibility)

    if (!visibilitySettings) {
      visibilityStatus.value = 'visible'
      return
    }

    const decision = $visibilityDecision(visibilitySettings, {
      debug: toValue(debug) === true,
    })

    if (decision.visible === null) {
      visibilityStatus.value = 'unknown'
      return
    }

    if (decision.visible === true) {
      visibilityStatus.value = 'visible'
      return
    }

    visibilityStatus.value = 'checking'
    await onDenied(decision)
  }

  onMounted(() => {
    watch(
      [
        () => toValue(visibility),
        () => cookieStore.countryCookie,
        () => cookieStore.languageCookie,
      ],
      () => {
        void runVisibilityCheck()
      },
      { immediate: true },
    )
  })

  return {
    visibilityStatus,
    runVisibilityCheck,
  }
}
